import type { BusinessPolicySnapshot } from '../policy/business-policy.ts';
import type { StaffPermission } from '../staff/staff-authority.ts';
import type { StoreSettingsPayload } from '../store-settings/store-settings.ts';
import type { CanonicalProduct } from '../menu/product.ts';
import {
  createSmtDeviceGateBootstrap,
  type SmtDeviceGateBootstrapSeed,
} from './smt-runtime-device-gate.ts';
import type {
  SmtRuntimeAdapterInputs,
  SmtRuntimeBootstrapResult,
} from './smt-runtime-bootstrap-contract.ts';

export interface MoreFunSmtDeviceGateEnvironment {
  readonly deviceId: string;
  readonly now: () => string;
  readonly nextId: SmtRuntimeAdapterInputs['nextId'];
}

const STORE_ID = 'morefun-yuen-long';
const DEVICE_GATE_OPERATOR_ID = 'device-gate-operator';
const DEVICE_GATE_ROOT_ID = 'device-gate-root';
const PRODUCT_ID = 'prod_f4';

const DEVICE_GATE_PERMISSIONS: readonly StaffPermission[] = [
  'availability.change',
  'availability.schedule',
  'table.open',
  'table.transfer',
  'table.merge',
  'table.split',
  'table.clear',
  'order.sensitive-correction',
  'payment.refund',
  'payment.correction',
];

const DEVICE_GATE_PRODUCT: CanonicalProduct = {
  id: PRODUCT_ID,
  status: 'active',
  names: {
    customer: 'F4 飯團',
    operations: 'F4 飯團',
  },
  pricing: {
    selectorKeys: ['riceball-c'],
  },
  optionGroups: [],
};

const DEVICE_GATE_STORE_SETTINGS: StoreSettingsPayload = {
  storeId: STORE_ID,
  storeName: '磨飯 Device Gate',
  timezone: 'Asia/Hong_Kong',
  currency: 'HKD',
  locale: 'zh-HK',
  businessHours: [
    { day: 'mon', closed: false, opensAt: '11:00', closesAt: '20:00' },
    { day: 'tue', closed: false, opensAt: '11:00', closesAt: '20:00' },
    { day: 'wed', closed: false, opensAt: '11:00', closesAt: '20:00' },
    { day: 'thu', closed: false, opensAt: '11:00', closesAt: '20:00' },
    { day: 'fri', closed: false, opensAt: '11:00', closesAt: '20:00' },
    { day: 'sat', closed: true },
    { day: 'sun', closed: false, opensAt: '11:00', closesAt: '20:00' },
  ],
  serviceModes: ['dine-in', 'takeaway'],
  dineInEnabled: true,
  takeawayEnabled: true,
  paymentMethodRefs: ['cash'],
  printRoutingRefs: ['device-gate-receipt'],
  channelRefs: ['store'],
};

const businessDateInHongKong = (timestamp: string): string => {
  const date = new Date(timestamp);
  if (!Number.isFinite(date.getTime())) throw new Error('SMT_DEVICE_GATE_NOW_INVALID');
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Hong_Kong',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const part = (type: Intl.DateTimeFormatPartTypes): string => {
    const value = parts.find((candidate) => candidate.type === type)?.value;
    if (!value) throw new Error('SMT_DEVICE_GATE_BUSINESS_DATE_UNAVAILABLE');
    return value;
  };
  return `${part('year')}-${part('month')}-${part('day')}`;
};

const policyFor = (businessDate: string): BusinessPolicySnapshot => ({
  policyVersion: `device-gate-policy:${businessDate}`,
  menuRevision: 'device-gate-menu:v1',
  pricingRevision: 'device-gate-pricing:v1',
  availabilityRevision: `device-gate-availability:${businessDate}`,
  rulesRevision: 'device-gate-rules:v1',
  currency: 'HKD',
  pricing: {
    productBaseMinor: { [PRODUCT_ID]: 4500 },
    modifierOptionMinor: {},
  },
  visibility: {
    customer: { [PRODUCT_ID]: true },
    operations: { [PRODUCT_ID]: true },
  },
  availability: {
    nodes: { [`product:${PRODUCT_ID}`]: 'available' },
    productRequirements: { [PRODUCT_ID]: [`product:${PRODUCT_ID}`] },
  },
  comboRules: [],
  modifierRules: [],
});

const seedFromEnvironment = (environment: MoreFunSmtDeviceGateEnvironment): SmtDeviceGateBootstrapSeed => {
  const now = environment.now();
  const businessDate = businessDateInHongKong(now);
  return {
    storeSettings: DEVICE_GATE_STORE_SETTINGS,
    storeReady: true,
    businessDate,
    basePolicy: policyFor(businessDate),
    products: [DEVICE_GATE_PRODUCT],
    categories: [{ categoryId: 'riceball', label: '飯團', productIds: [PRODUCT_ID] }],
    paymentMethods: [{ methodId: 'cash', label: '現金', kind: 'cash' }],
    rootActorId: DEVICE_GATE_ROOT_ID,
    operator: {
      staffId: DEVICE_GATE_OPERATOR_ID,
      displayName: 'Device Gate Operator',
      permissions: DEVICE_GATE_PERMISSIONS,
    },
    tables: [
      {
        tableId: 'T1',
        label: '1號枱',
        areaId: 'main',
        capacity: 2,
        position: 1,
        enabled: true,
        mergeWith: [],
      },
    ],
    queueClasses: [{ classId: 'small', prefix: 'A', minPartySize: 1, maxPartySize: 2 }],
    resolveFulfillmentPolicy: () => ({
      requiresPaymentBeforeProduction: false,
      requiredTasks: [],
    }),
    deviceId: environment.deviceId,
    initialServiceMode: 'takeaway',
    now: environment.now,
    nextId: environment.nextId,
  };
};

/**
 * Explicit non-production provider for the signed Android Runtime device gate.
 * The returned bootstrap always remains profile=device-gate with ephemeral
 * durability and a print test-double; it cannot establish Production truth.
 */
export function createMoreFunSmtDeviceGateBootstrap(
  environment: MoreFunSmtDeviceGateEnvironment,
): SmtRuntimeBootstrapResult {
  return createSmtDeviceGateBootstrap(seedFromEnvironment(environment));
}
